/* eslint-disable no-restricted-syntax */
import { IWorkplaceData, mapWorkplaces, Workplace } from './Workplace'
import { IPositionData, mapPositions, Position } from './Position'
import { IContactData, mapContacts, Contact } from './Contact'
import {
    mapTypeOfRepresentatives,
    TypeOfRepresentative,
    TypeOfRepresentativeData,
} from './TypeOfRepresentative'
import { IMemberData, mapMembers, Member } from './Member'

export type IProfileData = {
    Arbejdssteder: IWorkplaceData[]
    ErFavorit: boolean
    AntalNoter: number
    TotalAntalKollegaerBrugerDaekker: number
    TotalAntalKollegaerBrugerDaekkerTR: number
    TotalAntalKollegaerBrugerDaekkerFTR: number
    AntalFoaMedlemmerBrugerDaekker: number
    AntalFoaMedlemmerBrugerDaekkerTR: number
    AntalFoaMedlemmerBrugerDaekkerFTR: number
    OpdaterStatus: string
    Stillinger: IPositionData[]
    Medlemsident: number
    Fornavn: string
    Efternavn: string
    FuldeNavn: string
    FoedselsDato: string
    Alder: number
    Medlemsnummer: number
    Afdeling: { Indmeldelsesdato: string; Navn: string; Nummer: string }
    ProfilBillede: { Content: string; MimeType: string }
    Tillidshverv: TypeOfRepresentativeData[]
    KontaktoplysningerList: IContactData[]
    TillidhvervMedlemmerSuppleantForBruger: IMemberData[]
    BrugerSuppleantForTillidshvervMedlemmer: IMemberData[]
}

export function findPrimaryWorkPlace(member: Profile): Workplace {
    if (member.allWorkPlaces.length > 1) {
        const found = member.allWorkPlaces.find(
            (wordPlace) => wordPlace.isPrimaryWorkplace
        )
        if (found) {
            return found
        }
    }
    return member.workPlaces[0]
}

export function findMyWorkplaces(member: Profile): Workplace[] {
    let workPlaces: Workplace[] = []

    // eslint-disable-next-line no-restricted-syntax
    for (const position of member.positions) {
        const wpNumbers = position.numbers

        // eslint-disable-next-line guard-for-in
        for (const wpNumber of wpNumbers) {
            const wp = member.allWorkPlaces.find((w) => +w.number === +wpNumber)
            workPlaces = [...workPlaces, ...[wp]]
        }
    }

    return workPlaces
}

export class Profile {
    workPlaces: Workplace[] = []

    allWorkPlaces: Workplace[] = []

    isFavorite = false

    numberOfNotes = 0

    status = ''

    myProfileId = 0

    firstName = ''

    lastName = ''

    fullName = ''

    roles: string[] = []

    personalInfo = {
        birthdate: '',
        age: 0,
    }

    membershipInfo = {
        date: '',
        name: '',
        number: '',
    }

    profilePicture = {
        content: '',
        mimeType: '',
    }

    memberNumber = 0

    totalNumberOfPeopleUserCovers = 0

    totalNumberOfPeopleUserCoversTR = 0

    totalNumberOfPeopleUserCoversFTR = 0

    numberFoaMemberUserCovers = 0

    numberFoaMemberUserCoversTR = 0

    numberFoaMemberUserCoversFTR = 0

    positions: Position[] = []

    typeOfRepresentatives: TypeOfRepresentative[] = []

    contactInfoList: Contact[] = []

    primaryWorkPlace: Workplace | undefined = undefined

    privateContact: Contact | undefined = undefined

    workContact: Contact | undefined = undefined

    trForSelf: Member[] | undefined

    trForOthers: Member[] | undefined

    public static build = (data: IProfileData): Profile => {
        const myProfile = new Profile()

        myProfile.allWorkPlaces = mapWorkplaces(data.Arbejdssteder)
        myProfile.isFavorite = data.ErFavorit
        myProfile.numberOfNotes = data.AntalNoter
        myProfile.status = data.OpdaterStatus
        myProfile.positions = mapPositions(data.Stillinger)
        myProfile.myProfileId = data.Medlemsident
        myProfile.firstName = data.Fornavn
        myProfile.lastName = data.Efternavn
        myProfile.fullName = data.FuldeNavn
        myProfile.memberNumber = data.Medlemsnummer
        myProfile.totalNumberOfPeopleUserCovers =
            data.TotalAntalKollegaerBrugerDaekker

        myProfile.totalNumberOfPeopleUserCoversTR =
            data.TotalAntalKollegaerBrugerDaekkerTR
        myProfile.totalNumberOfPeopleUserCoversFTR =
            data.TotalAntalKollegaerBrugerDaekkerFTR
        myProfile.numberFoaMemberUserCovers =
            data.AntalFoaMedlemmerBrugerDaekker
        myProfile.numberFoaMemberUserCoversTR =
            data.AntalFoaMedlemmerBrugerDaekkerTR
        myProfile.numberFoaMemberUserCoversFTR =
            data.AntalFoaMedlemmerBrugerDaekkerFTR
        myProfile.personalInfo.age = data.Alder
        myProfile.personalInfo.birthdate = data.FoedselsDato
        myProfile.profilePicture.content = data?.ProfilBillede?.Content
        myProfile.profilePicture.mimeType = data?.ProfilBillede?.MimeType
        myProfile.membershipInfo.date = data.Afdeling.Indmeldelsesdato
        myProfile.membershipInfo.name = data.Afdeling.Navn
        myProfile.membershipInfo.number = data.Afdeling.Nummer
        myProfile.typeOfRepresentatives = mapTypeOfRepresentatives(
            data.Tillidshverv
        )
        myProfile.contactInfoList = data.KontaktoplysningerList
            ? mapContacts(data.KontaktoplysningerList)
            : []

        myProfile.workPlaces = findMyWorkplaces(myProfile)

        myProfile.primaryWorkPlace = findPrimaryWorkPlace(myProfile)

        // Map Contacts.
        myProfile.privateContact = myProfile.contactInfoList.find(
            (c) => c.contactInfoType === 'TR'
        )

        myProfile.workContact = myProfile.contactInfoList.find(
            (c) => c.contactInfoType === 'Arbejde'
        )

        myProfile.trForOthers = mapMembers(
            data.BrugerSuppleantForTillidshvervMedlemmer
        )

        myProfile.trForSelf = mapMembers(
            data.TillidhvervMedlemmerSuppleantForBruger
        )

        return myProfile
    }
}

export function mapProfiles(data: IProfileData[]): Profile[] {
    return data && data.map((myProfile) => Profile.build(myProfile))
}
